import { linearGradientDef } from '@nivo/core'
import { type Datum, ResponsiveLine } from '@nivo/line'
import type React from 'react'
import TooltipRow from '../../../../../components/TooltipRow'
import { colorByPerformance } from '../../../../../utils/colorByPerformance'
import { formatNumber } from '../../../../../utils/formatters'
import { InvestorChartData } from '../InvestorScreen'

interface LineChartProps {
  data?: InvestorChartData[]
  currencyIsoCode?: string
  viewGranularity?: 'daily' | 'monthly'
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  currencyIsoCode,
  viewGranularity = 'monthly',
}) => {
  // Format x-axis labels based on granularity
  const formatXAxisLabel = (item: InvestorChartData) => {
    if (viewGranularity === 'daily') {
      return item.month.format('MMM DD')
    } else {
      return item.month.format('MMMM')
    }
  }

  // Format tooltip header based on granularity
  const formatTooltipHeader = (item: InvestorChartData) => {
    if (viewGranularity === 'daily') {
      return item.month.format('MMMM DD, YYYY')
    } else {
      return item.month.format('MMMM YYYY')
    }
  }

  return (
    <div className="bg-brand-light-card dark:bg-brand-dark-card col-span-full relative rounded-xl border-[1px] border-solid border-black/10 dark:border-brand-dark-card-border">
      <div className="h-[500px]">
        <ResponsiveLine
          data={[
            {
              id: 'Id',
              data:
                data?.map(
                  (item, i) =>
                    ({
                      x: formatXAxisLabel(item),
                      y: item.data?.endOfMonthCapital ?? null,
                      realIndex: i,
                    }) as Datum
                ) ?? [],
            },
          ]}
          enableSlices="x"
          enableArea={true}
          enableGridX={true}
          enableGridY={true}
          colors="#29383B"
          margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
          defs={[
            linearGradientDef('gradientA', [
              { offset: 0, color: 'inherit' },
              { offset: 100, color: 'inherit', opacity: 0 },
            ]),
          ]}
          fill={[{ match: '*', id: 'gradientA' }]}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            stacked: true,
            reverse: false,
          }}
          yFormat=" >-.2f"
          axisTop={null}
          animate={false}
          axisRight={null}
          // Adjust axis bottom for daily view to prevent overcrowding
          axisBottom={
            viewGranularity === 'daily' && data && data.length > 30
              ? {
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  // Show fewer ticks for daily view with lots of data
                  tickValues: Math.ceil(data.length / 10), // Show approximately 10 ticks
                }
              : viewGranularity === 'daily' && data && data.length > 15
                ? {
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -30,
                  }
                : undefined
          }
          tooltip={point => {
            return (
              <div className="bg-black text-white">
                {point.point.data.y.toString()}
              </div>
            )
          }}
          sliceTooltip={point => {
            //@ts-ignore
            const index = point.slice.points[0].data.realIndex

            const currData = data ? data[index].data : undefined

            const performancePercent = currData?.performancePercent ?? 0

            const performancePercentToDate =
              (currData?.performancePercentToDate ?? 0) * 100

            const endOfMonthCapital = currData?.endOfMonthCapital ?? 0

            const periodLabel =
              viewGranularity === 'daily' ? 'daily' : 'monthly'

            return (
              <div className="bg-brand-light-card border dark:bg-brand-dark-background border-solid border-brand-light-card-border dark:border-brand-dark-card-border shadow-lg px-4 py-3 rounded-lg text-sm">
                <p className="font-semi flex justify-between">
                  <p className="dark:text-white">
                    {data ? formatTooltipHeader(data[index]) : ''}
                  </p>

                  <span
                    className={`font-semi ${colorByPerformance(
                      performancePercent
                    )}`}
                  >
                    {(performancePercent * 100).toFixed(2)}%{' '}
                    {viewGranularity === 'daily' ? '(daily)' : '(monthly)'}
                  </span>
                </p>
                <TooltipRow
                  description="Account value"
                  value={
                    <span>
                      <span className={colorByPerformance(endOfMonthCapital)}>
                        {formatNumber(
                          endOfMonthCapital,
                          {
                            currencyType: 'FiatCurrency',
                            isoCode: currencyIsoCode ?? 'EUR',
                          },
                          2
                        )}
                      </span>{' '}
                      <span
                        className={`${colorByPerformance(
                          performancePercentToDate
                        )}`}
                      >
                        ({performancePercentToDate.toFixed(2)}
                        %)
                      </span>
                    </span>
                  }
                />
                {!!currData?.performanceFee && (
                  <TooltipRow
                    description="Performance fee"
                    value={formatNumber(
                      currData?.performanceFee,
                      {
                        currencyType: 'FiatCurrency',
                        isoCode: currencyIsoCode ?? 'EUR',
                      },
                      2
                    )}
                  />
                )}
                {!!currData?.deposited && (
                  <TooltipRow
                    description={`Deposited this ${periodLabel === 'daily' ? 'day' : 'month'}`}
                    value={formatNumber(
                      currData?.deposited,
                      {
                        currencyType: 'FiatCurrency',
                        isoCode: currencyIsoCode ?? 'EUR',
                      },
                      2
                    )}
                  />
                )}
                {!!currData?.withdrawn && (
                  <TooltipRow
                    description={`Withdrawn this ${periodLabel === 'daily' ? 'day' : 'month'}`}
                    value={formatNumber(
                      currData?.withdrawn,
                      {
                        currencyType: 'FiatCurrency',
                        isoCode: currencyIsoCode ?? 'EUR',
                      },
                      2
                    )}
                  />
                )}
              </div>
            )
          }}
          pointSize={5}
          pointColor="#29383B"
          pointLabelYOffset={-12}
          useMesh={true}
        />
      </div>
    </div>
  )
}

export default LineChart
